import React, {useEffect} from "react";
import {Navigate, useNavigate, useParams} from "react-router-dom";
import {makeStyles} from "@material-ui/core/styles";
import {WithOrganization} from "../components/organizations/organization-context";
import ReactsCard from "../components/ui/ReactsCard";
import {useTranslation} from 'react-i18next';
import {emailRegex, rand, useQuery} from "../utils/function-util";
import {ContactInviteEditor} from "../components/machines/shared/machine-contacts/ContactInviteEditor";
import {TabData, Wizard} from "../components/_shared/wizard/Wizard";
import {Members} from "../components/members/Members";
import {WizardNavigation, WizardNavigationButton} from "../components/_shared/wizard/WizardNavigation";
import * as _ from 'lodash';
import {SearchedMember} from "../models/membership.models";
import Checkbox from "@material-ui/core/Checkbox";
import {FormStatus, KeyValue} from "../models/shared";
import Typography from "@material-ui/core/Typography";
import {
    ContactError,
    ContactToAdd,
    isImportedItem,
    isSelectedItem,
    MachineContactModel
} from "../models/machine.models";
import Grid from "@material-ui/core/Grid";
import {ContactInviteSummary} from "../components/machines/shared/machine-contacts/ContactInviteSummary";
import {addContacts} from "../api/machine-functions";
import {searchOrganizationMembers} from "../api/organization-functions";
import {httpPost} from "../api/http-functions";
import {FormControlLabel} from "@material-ui/core";

const useStyles = makeStyles(theme => ({
    root: {
        margin: theme.spacing(4),
        '& > *': {
            'margin-bottom': theme.spacing(4)
        }
    },
    selectedCount: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end'
    }
}));

const buildContact: ([email, firstName, lastName]?: string[]) => ContactToAdd = ([email, firstName, lastName] = ['', '', '']) => ({
    email,
    firstName,
    lastName
});

const buildFormItem = (contact: ContactToAdd, id: string | number | undefined = undefined, status: FormStatus = FormStatus.none): MachineContactModel => ({
    id: !!id ? id : rand(),
    data: contact,
    errors: {},
    errorMessage: "",
    status: status || FormStatus.deletable,
});

export const MachineAddContactsView: React.FC<WithOrganization> = ({currentOrganization}) => {
    const classes = useStyles();
    const navigate = useNavigate();
    const {machineId, organizationUnitId} = useParams<{ machineId: string, organizationUnitId: string }>();
    const query = useQuery();
    const machineName = query.get('machineName');
    const {t} = useTranslation();
    const [forms, setForms] = React.useState<MachineContactModel[]>([]);
    const [allUsersCount, setAllUsersCount] = React.useState<null|number>(null);

    useEffect(() => {
        getAllUsersCount();
    });

    if (!organizationUnitId) {
        return <Navigate to='/organization-units' replace={false}/>;
    }

    const goBack = () => {
        navigate(`/organization-units/${organizationUnitId}/machines/${machineId}/contacts?machineName=${machineName}`);
    };

    const isProcessing = forms.some(f => f.status === FormStatus.processing);
    const isProcessed = forms.length > 0 && forms.every(f => f.status === FormStatus.successful || f.status === FormStatus.failed);

    const submitAll = async () => {
        setForms(prev => prev
            .filter(f => f.status !== FormStatus.successful
                && f.status !== FormStatus.processing)
            .map(f => ({...f, status: FormStatus.processing}))
        );

        for (let i = 0; i < forms.length; i++) {
            await submitItem(forms[i]);
        }
    };

    const submitItem = async (item: MachineContactModel) => {
        if (!item) return;

        try {
            const success = await addContacts(machineId as string, [item.data]);
            if (success.Result === addContactResult.success) {
                updateItemStatus(item.id, FormStatus.successful);
            } else {
                const itemResults = success.ContactElementResponses.filter(r => r.Email === item.data.email);
                if (itemResults.length > 0) {
                    const itemResult = itemResults[0];
                    if (itemResult.Result === addContactResult.success) {
                        updateItemStatus(item.id, FormStatus.successful);
                    } else if (itemResult.Result === addContactResult.sourceNotSupportPairing) {
                        updateItem(item.id, {status: FormStatus.failed, errorMessage: 'Machine.Contacts.AddContacts.SourceMachineCannotBePaired'});
                    } else if (itemResult.Result === addContactResult.targetNotSupportPairing) {
                        updateItem(item.id, {status: FormStatus.failed, errorMessage: 'Machine.Contacts.AddContacts.TargetMachineCannotBePaired'});
                    } else {
                        updateItemStatus(item.id, FormStatus.failed);
                    }
                } else {
                    updateItemStatus(item.id, FormStatus.failed);
                }
            }
        } catch (err) {
            const errorType: any = _.get(err, 'response.data.error.type');
            let errorMessage : string | null = null;

            switch (errorType){
                case machineErrors.sourceMachineCannotBePaired:
                    errorMessage = 'Machine.Contacts.AddContacts.SourceMachineCannotBePaired';
                    break;
                case machineErrors.targetMachineCannotBePaired:
                    errorMessage = 'Machine.Contacts.AddContacts.TargetMachineCannotBePaired';
                    break;
                default:
                    errorMessage = `_Common.Api.Messages.${errorType}`;
            }

            updateItem(item.id, {status: FormStatus.failed, errorMessage});
        }
    };

    const machineErrors = {
        sourceMachineCannotBePaired: "SourceMachineCannotBePaired",
        targetMachineCannotBePaired: "TargetMachineCannotBePaired"
    };

    enum addContactResult {
        unknown = 0,
        success = 1,
        unauthorized = 2,
        sourceNotSupportPairing = 3,
        targetNotSupportPairing = 4,
        failed = 99
    }

    const validateField = (field: any, value: any) => {
        const errors: KeyValue<string> = {};
        if (field === 'email') {
            errors[field] = emailRegex.test(value) ? null : 'EmailIsInvalid';
        }
        else {
            errors[field] = !value ? 'FieldIsRequired' : null;
        }
        return _.isEmpty(errors[field]) ? null : errors;
    };

    const validateRecord = (record: ContactToAdd): ContactError => ["email", "firstName", "lastName"]
        .map(k => validateField(k, _.get(record, k)))
        .reduce(_.merge, {});

    const validateAll = (formsToValidate: MachineContactModel[]) => formsToValidate.map(f => {
        const errors = validateRecord(f.data);
        return {...f, errors}
    });

    const addItem = (data: ContactToAdd, userId: string | number | undefined = undefined): (number | string) => {
        let id: number | string = userId || -rand();

        setForms(prevState => {
            const index = _.findIndex(prevState, i => i.data.email === data.email)
            if (index >= 0) {
                id = prevState[index].id;
                return [...prevState];
            } else {
                const c = buildFormItem(data, id);
                id = c.id;
                return [...prevState, c];
            }
        });

        return id;
    }

    const updateItemData = (id: number | string, fields: Partial<ContactToAdd>): (ContactError | null) => {
        let errors: ContactError = {};

        setForms(prevState => {
            const idx = _.findIndex(prevState, f => f.id === id);
            const item = prevState[idx];
            const newData = {...item.data, ...fields};
            errors = validateRecord(newData);
            const newItem = {
                ...item,
                data: newData,
                errors: errors
            };
            return [...prevState.slice(0, idx), newItem, ...prevState.slice(idx + 1)];
        });

        return _.isEmpty(errors) ? null : errors;
    }

    const updateItemStatus = (id: number | string, status: FormStatus) => updateItem(id, {status});

    const updateItem = (id: number | string, patch: any) => setForms(prev => {
        const index = prev.findIndex(f => f.id === id);
        if (index <= -1) return prev;
        const result = [...prev];
        result[index] = _.merge({}, prev[index], patch);
        return result;
    });

    const removeItem = (id: number | string) => setForms(prevState => {
        const index = _.findIndex(prevState, i => i.id === id);
        return index >= 0 ?
            [...prevState.slice(0, index), ...prevState.slice(index + 1)] :
            prevState
    });

    const handleSelectionChanged = (member: SearchedMember, checked: boolean) => {
        if (checked) {
            addItem(buildContact([member.email, member.userFirstName, member.userLastName]), member.userId);
        } else {
            removeItem(member.userId);
        }
    }

    const getUsersQuery = (size: number, page = 0) => {
        return searchOrganizationMembers(httpPost)({organizationId: currentOrganization.id, page, size, filter: ''});
    };

    const getAllUsersCount = async () => {
        if (allUsersCount == null) {
            let firstUserResult = await getUsersQuery(1)
            const total = firstUserResult.total;
            setAllUsersCount(total);
            return total;
        } else {
            return allUsersCount;
        }
    };

    const getAllUsers = async () => {
        const total = await getAllUsersCount();
        return (await getUsersQuery(total)).items;
    }
    
    const handleSelectAllChange = async (checked: boolean) => {
        const clearSelection = () => {
            setForms(_ => []);
        };

        const selectAll = async () => {
            const users: SearchedMember[] = await getAllUsers();
            for (let user of users) {
                if (!forms.filter(f => f.id === user.userId).length) {
                    addItem(buildContact([user.email, user.userFirstName, user.userLastName]), user.userId);
                }
            }
        };
        
        if (checked) {
            await selectAll();
        } else {
            clearSelection();
        }
    }

    const handleTabChanged = (lastIndex: number, onChangeTab: (newTab: number) => void) =>
        (newTabIndex: number) => {
            if (newTabIndex === lastIndex - 1) {
                if (!forms.some(isImportedItem)) {
                    setForms(prevState => [...prevState, buildFormItem(buildContact())]);
                }
            } else {
                setForms(prev => {
                    const filtered = validateAll([...prev.filter(i => isSelectedItem(i) || isImportedItem(i) && (!!i.data.email || !!i.data.firstName || !!i.data.lastName))]);
                    return newTabIndex === lastIndex ?
                        filtered.sort((l: MachineContactModel, r: MachineContactModel) => {
                            const lId = typeof l.id;
                            const rId = typeof r.id;
                            return rId.localeCompare(lId);
                        }) :
                        filtered;
                });
            }

            if (newTabIndex === lastIndex && forms.some(f => _.negate(_.isEmpty)(f.errors))) {
                return;
            }

            return onChangeTab(newTabIndex);
        }

    const stepBySelection: TabData = {
        labelKey: "Machine.Contacts.AddContacts.Tabs.BySelection.Title",
        key: "add-machine-contacts-by-selection",
        preserveTab: true,
        isDisabled: true,
        childComponent: (index, lastIndex, onChangeTab) =>
            <>
                <Grid item xs={12} className={classes.selectedCount}>
                    <Typography variant="subtitle1">
                        {t('Machine.Contacts.AddContacts.Tabs.BySelection.Selected', {count: forms.filter(f => typeof f.id === 'string').length})}
                    </Typography>
                </Grid>
                <Members organizationId={currentOrganization.id}
                         actionForAll={<Grid item xs={12}>
                             <FormControlLabel control={<Checkbox onChange={e => handleSelectAllChange(e.target.checked)}
                                                                  checked={forms.filter(f => typeof f.id === 'string').length === allUsersCount}
                                                                  color="primary" />}
                                               label={t('Machine.Contacts.AddContacts.Tabs.BySelection.SelectAll')} />
                         </Grid>}
                         actions={(member: SearchedMember) =>
                             <Checkbox checked={_.some(forms, i => i.id === member.userId)}
                                       onChange={e => handleSelectionChanged(member, e.target.checked)}
                                       color="primary"
                                       inputProps={{'aria-label': 'primary checkbox'}}/>}
                />
                <WizardNavigation onChangeTab={handleTabChanged(lastIndex, onChangeTab)}
                                  onClose={goBack}
                                  index={index}
                                  lastIndex={lastIndex}/>
            </>
    }

    const stepByEmail: TabData = {
        labelKey: "Machine.Contacts.AddContacts.Tabs.ByEmail.Title",
        key: "add-machine-contacts-by-email",
        preserveTab: true,
        isDisabled: true,
        childComponent: (index, lastIndex, onChangeTab) =>
            <>
                <ContactInviteEditor items={forms}
                                     onAddItem={addItem}
                                     onRemoveItem={removeItem}
                                     onUpdateItem={updateItemData}
                                     buildItemData={buildContact}/>
                <WizardNavigation onChangeTab={handleTabChanged(lastIndex, onChangeTab)}
                                  onClose={goBack}
                                  disabledButtons={
                                      forms.some(f => isImportedItem(f) && _.values(f.errors).length > 0) ?
                                          new Set([WizardNavigationButton.Next]) : undefined
                                  }
                                  index={index}
                                  lastIndex={lastIndex}/>
            </>
    }

    const stepProcessing: TabData = {
        labelKey: "Machine.Contacts.AddContacts.Tabs.Validation.Title",
        key: "add-machine-contact-processing",
        preserveTab: true,
        isDisabled: true,
        childComponent: (index, lastIndex, onChangeTab) =>
            <>
                <ContactInviteSummary forms={forms}
                                      onUpdated={updateItemData}
                                      onRemoved={removeItem}/>
                <WizardNavigation onSave={submitAll}
                                  onClose={goBack}
                                  onChangeTab={handleTabChanged(lastIndex, onChangeTab)}
                                  index={index}
                                  disabledButtons={forms.length === 0 ?
                                      new Set<WizardNavigationButton>([WizardNavigationButton.Save])
                                      : isProcessing ?
                                          new Set<WizardNavigationButton>([
                                              WizardNavigationButton.Close,
                                              WizardNavigationButton.Save,
                                              WizardNavigationButton.Previous])
                                          : isProcessed ?
                                              new Set<WizardNavigationButton>([
                                                  WizardNavigationButton.Save,
                                                  WizardNavigationButton.Previous
                                              ]) : undefined}
                                  lastIndex={lastIndex}/>
            </>
    }

    return (
        <div className={classes.root}>
            <ReactsCard title={t('Machine.Contacts.AddContacts.Title')}>
                <Wizard tabs={[stepBySelection, stepByEmail, stepProcessing]}/>
            </ReactsCard>
        </div>
    )
};
